import { Injectable } from '@angular/core';
import { scormKeySchema, scormSetValueSchema } from '@shared/ragai.schemas';
import { z } from 'zod';

type ScormAPI = {
  LMSSetValue: (key: string, value: string) => void;
  LMSFinish: () => string;
  LMSIsInitialized: () => string;
  LMSCommit: () => string;
  LMSInitialize: () => string;
  LMSGetValue: (key: string) => string | undefined;
};
/**
 * Brückt SCORM-LMS-API-Aufrufe (`RAGAI.SCORM`), mit Fallback auf eine Dummy-Implementierung außerhalb eines LMS.
 * @remarks
 * Läuft der Bot **nicht** in einem SCORM-LMS (erkannt über `runsinlms`-URL-Parameter des Eltern-/Großeltern-Fensters),
 * liefert {@link getApiHandle} intern eine Fake-API zurück, die alle Werte nur in einem lokalen Objekt hält statt
 * an ein echtes LMS zu melden - Aufrufe schlagen also nie fehl, haben aber außerhalb eines LMS keine reale Wirkung.
 * Vor Aktionen, die auf ein echtes LMS angewiesen sind, sollte `RAGAI.WAITFOR.scormInited()` abgewartet werden.
 */
@Injectable({
  providedIn: 'root'
})
export class ScormManagerService {

  /**
   * Prüft, ob der Bot aktuell eingebettet in einem LMS läuft.
   * @returns `true`, wenn der Bot in einem LMS läuft.
   */
  public runsInLms() {
    try {
      const windowParentParams = new URLSearchParams(window.parent.location.search);
      if (windowParentParams.get('runsinlms')) {
        return true;
      }
      const windowParentParentParams = new URLSearchParams(window.parent.parent.location.search);
      if (windowParentParentParams.get('runsinlms')) {
        return true;
      }
    } catch (_e: unknown) {
      return false;
    }
    return false;

  }

  private fakeAPI: ScormAPI = {
    LMSSetValue: (key: string, value: string) => {
      this.fakeSCORM[key] = value;
    },
    LMSFinish: () => {
      return "true";
    },
    LMSIsInitialized: () => {
      return "true";
    },
    LMSCommit: () => {
      return "true";
    },
    LMSInitialize: () => {
      return "true";
    },
    LMSGetValue: (key: string) => {
      return this.fakeSCORM[key];
    }
  };

  private fakeSCORM: Record<string, string> = {
    'cmi.suspend_data': "",
    'cmi.core.student_name': "%SCORM_STUDENT_NAME%",
    'cmi.core.student_id': "%SCORM_STUDENT_ID%",
    'cmi.core.score.raw': "",
    'cmi.core.score.min': "",
    'cmi.core.score.max': "",
    'cmi.core.lesson_location': "",
    'cmi.core.lesson_status': "",
    'cmi.interactions._count': ""
  };

  /**
   * Prüft, ob aktuell die Dummy-SCORM-API verwendet wird (kein echtes LMS gefunden).
   * @returns `true`, wenn die Dummy-API aktiv ist.
   */
  public isFakeScorm() {
    return this.getApiHandle() === this.fakeAPI;
  }

  private getApiHandle() {


    if (this.runsInLms()) {

      const API = (window.parent as { API?: ScormAPI }).API;
      if (API) {
        return API;
      }
    }






    console.warn("Using Dummy Scrom API because no LMS Api found");

    return this.fakeAPI;
  }

  /**
   * Setzt einen SCORM-Datenwert.
   * @param key Der SCORM-Schlüssel, oder ein Objekt gemäß `scormSetValueSchema`.
   * @param value Der zu setzende Wert.
   */
  public LMSSetValue(key: string | z.input<typeof scormSetValueSchema>, value?: string): void {
    const parsed = scormSetValueSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value;
      key = parsed.data.key;
    }
    this.getApiHandle().LMSSetValue(key as string, value ?? '');
  }

  /**
   * Beendet die SCORM-Sitzung.
   * @returns `"true"` bei Erfolg.
   */
  public LMSFinish() {
    return this.getApiHandle().LMSFinish();
  }

  /**
   * Prüft, ob die SCORM-API initialisiert wurde.
   * @returns `"true"`, wenn initialisiert.
   */
  public LMSIsInitialized() {
    return this.getApiHandle().LMSIsInitialized();
  }

  /**
   * Bestätigt (committet) alle gesetzten SCORM-Werte an das LMS.
   * @returns `"true"` bei Erfolg.
   */
  public LMSCommit() {
    return this.getApiHandle().LMSCommit();
  }

  /**
   * Initialisiert die SCORM-API-Verbindung zum LMS.
   * @returns `"true"` bei Erfolg.
   */
  public LMSInitialize() {
    return this.getApiHandle().LMSInitialize();
  }

  /**
   * Liest einen SCORM-Datenwert.
   * @param key Der SCORM-Schlüssel, oder ein Objekt gemäß `scormKeySchema`.
   * @returns Der gespeicherte Wert, oder `undefined`.
   */
  public LMSGetValue(key: string | z.input<typeof scormKeySchema>) {
    const parsed = scormKeySchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      key = parsed.data.key;
    }
    return this.getApiHandle().LMSGetValue(key as string);
  }
  //wollen wir den scorm hook noch oder läuft das über events (Die von aussen eh getriggert werden)
  /**
   * @internal Platzhalter für einen künftigen SCORM-Hook; aktuell ungenutzt (läuft über Events).
   * @param _hook Name des Hooks.
   * @param _params Hook-Parameter.
   */
  public scormHook(_hook: string, _params: unknown) {
    console.debug("RAGAI.SCORM.LMSHook not used ");
  }
}
